import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@core-ui/Components/ui/dialog.tsx';
import { Button } from '@core-ui/Components/ui/button.tsx';
import { AlertTriangle } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';

interface ConfirmDeleteNumberDialogProps {
  open: boolean;
  number?: string;
  loading?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  titleKey?: string;
  confirmKey?: string;
  cancelKey?: string;
  disableCloseOnLoading?: boolean;
}

export default function ConfirmDeleteNumberDialog ({
  open,
  number,
  loading = false,
  onCancel,
  onConfirm,
  titleKey = 'adminConsole.numberDeleteDialog.title',
  confirmKey = 'adminConsole.numberDeleteDialog.confirm',
  cancelKey = 'adminConsole.numberDeleteDialog.cancel',
  disableCloseOnLoading = true,
}: ConfirmDeleteNumberDialogProps): React.JSX.Element {
  const { t } = useTranslation();

  const handleOpenChange = (isOpen: boolean): void => {
    if (disableCloseOnLoading && loading) {
      return;
    }
    if (!isOpen) {
      onCancel();
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={handleOpenChange}
    >
      <DialogContent
        className="max-w-lg rounded-2xl shadow-lg border border-border bg-card text-foreground p-6"
        onEscapeKeyDown={(e) => {
          if (disableCloseOnLoading && loading) {
            e.preventDefault();
          }
        }}
        onPointerDownOutside={(e) => {
          if (disableCloseOnLoading && loading) {
            e.preventDefault();
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !loading) {
            e.preventDefault();
            onConfirm();
          }
        }}
      >
        <DialogHeader className="mb-2">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-destructive/30 bg-destructive/10 text-destructive">
              <AlertTriangle className="h-5 w-5" />
            </span>
            <DialogTitle className="text-lg font-semibold">
              {t(titleKey, { defaultValue:'Delete number {{number}}', number })}
            </DialogTitle>
          </div>
        </DialogHeader>

        <DialogDescription>
          <div className="leading-relaxed text-md text-muted-foreground">
            {(number != null) ? (
              <Trans
                i18nKey="adminConsole.numberDeleteDialog.bodyWithName"
                components={{ strong: <span className="font-semibold" /> }}
                values={{ name: number }}
                defaults="Are you sure you want to delete <strong>{{name}}</strong>? This action cannot be undone."
              />
            ) : (t('adminConsole.numberDeleteDialog.bodyGeneric')
            )}
            <span> {t('adminConsole.numberDeleteDialog.disclaimer')}</span>
          </div>
        </DialogDescription>

        <div className="flex justify-end gap-2 mt-6">
          <Button
            data-qa-id="cancel-delete-number-button"
            variant="outline"
            onClick={onCancel}
            disabled={loading}
            className="border border-border text-foreground hover:bg-muted"
            type="button"
            autoFocus
          >
            {t(cancelKey, 'Cancel')}
          </Button>
          <Button
            data-qa-id="delete-number-form-button"
            variant="destructive"
            onClick={onConfirm}
            disabled={loading}
            className="text-primary-foreground"
            type="button"
          >
            {loading
              ? t('adminConsole.numberDeleteDialog.deleting', 'Deleting…')
              : t(confirmKey, 'Delete')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
